import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, NotebookPen, Trash2 } from "lucide-react";

export default function HomeworkPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const [scope, setScope] = useState({ branch_id: "", class_name: "", section: "A" });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: "", title: "", details: "", due_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10) });
  const today = new Date().toISOString().slice(0, 10);

  async function load() {
    const p = isParent ? {} : { branch_id: scope.branch_id, class_name: scope.class_name || undefined, section: scope.section || undefined };
    setRows((await api.get("/homework", { params: p })).data);
  }
  useEffect(() => { if (isParent || scope.branch_id) load().catch(() => {}); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    try { await api.post("/homework", { ...scope, ...f }); toast.success("Homework assigned"); setOpen(false); setF({ ...f, title: "", details: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Academics" title="Homework" subtitle={isParent ? "Homework and class work for your child." : "Assign homework to a class."}>
        {!isParent && <Button data-testid="new-homework-btn" onClick={() => setOpen(true)} disabled={!scope.class_name} className="btn-brand"><Plus size={16} className="mr-1" /> Assign</Button>}
      </PageHeader>
      {!isParent && <div className="mb-5"><ClassPicker value={scope} onChange={setScope} /></div>}
      {rows.length === 0 ? <Empty testid="homework-empty" icon={NotebookPen} text="No homework yet." /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((h) => (
            <div key={h.id} data-testid={`homework-${h.id}`} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">{h.subject}</Badge>
                <span className={`text-xs font-semibold ${h.due_date < today ? "text-slate-400" : "text-rose-600"}`}>Due {fmtDate(h.due_date)}</span>
              </div>
              <div className="font-display font-bold text-slate-900 mt-2">{h.title}</div>
              {h.details && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{h.details}</p>}
              <div className="flex justify-between items-center mt-3 text-xs text-slate-500">
                <span>{h.class_name} {h.section} • {h.posted_by_name}</span>
                {!isParent && <button data-testid={`delete-homework-${h.id}`} onClick={async () => { await api.delete(`/homework/${h.id}`); load(); }} className="hover:text-red-600"><Trash2 size={14} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Assign homework • {scope.class_name} {scope.section}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subject</Label><Input data-testid="homework-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="mt-1" /></div>
              <div><Label>Due date</Label><Input data-testid="homework-due" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Title</Label><Input data-testid="homework-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" /></div>
            <div><Label>Details</Label><Textarea data-testid="homework-details" value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} className="mt-1" /></div>
            <Button data-testid="homework-save" onClick={save} disabled={!f.subject || !f.title} className="w-full btn-brand">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
